// ---------------------------------------------------------------------------
// NoxVerify typed HTTP client
// ---------------------------------------------------------------------------

import type {
  ApiSuccessResponse,
  ApiListResponse,
  ApiErrorResponse,
  PaginationParams,
  admin,
  applicant,
} from '@noxverify/api-contracts';
import type { ApiClientConfig, RequestOptions, ApiClientError } from './types.js';

type IdempotentRequestOptions = RequestOptions & { idempotencyKey: string };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Convert a params object to a flat `Record<string, string>` suitable for
 * URL search parameters, omitting `undefined` values.
 */
function toSearchParams(
  params?: object,
): Record<string, string> | undefined {
  if (!params) return undefined;
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      result[key] = String(value);
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export class NoxVerifyClient {
  private config: ApiClientConfig;

  constructor(config: ApiClientConfig) {
    this.config = config;
  }

  // ---- Generic request ----

  private async request<T>(
    method: string,
    path: string,
    options?: RequestOptions & {
      body?: unknown;
      params?: Record<string, string>;
    },
  ): Promise<T> {
    const url = new URL(path, this.config.baseUrl);

    if (options?.params) {
      for (const [key, value] of Object.entries(options.params)) {
        url.searchParams.set(key, value);
      }
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(this.config.authToken
        ? { Authorization: `Bearer ${this.config.authToken}` }
        : {}),
      ...(this.config.correlationId
        ? { 'x-correlation-id': this.config.correlationId }
        : {}),
      ...(options?.idempotencyKey
        ? { 'idempotency-key': options.idempotencyKey }
        : {}),
      ...options?.headers,
    };

    const response = await fetch(url.toString(), {
      method,
      headers,
      body: options?.body ? JSON.stringify(options.body) : undefined,
      signal: options?.signal,
      // Send the nv_session / Cf-Access cookie on cross-origin admin API
      // calls (SPA on admin.noxverify.com → API on api.noxverify.com). The
      // CORS layer echoes the exact origin + Access-Control-Allow-Credentials,
      // so credentialed cross-origin requests are accepted. Harmless for the
      // Bearer-token (portal) and public (marketing) paths.
      credentials: 'include',
    });

    const json = (await response.json()) as Record<string, unknown>;

    if (!response.ok) {
      const errorBody = json as unknown as ApiErrorResponse;
      const error: ApiClientError = {
        status: response.status,
        code: errorBody.error?.code ?? 'unknown',
        message: errorBody.error?.message ?? 'Unknown error',
        correlationId: (errorBody.correlationId as string) ?? null,
      };
      this.config.onError?.(error);
      throw error;
    }

    return json as T;
  }

  // ---- Admin API ----

  admin = {
    /** List tenants — `GET /v1/admin/tenants`. */
    listTenants: (
      params?: admin.ListTenantsParams,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.TenantSummary>>(
        'GET',
        '/v1/admin/tenants',
        { params: toSearchParams(params), ...options },
      ),

    /** Create a tenant — `POST /v1/admin/tenants`. */
    createTenant: (
      body: admin.CreateTenantRequest,
      options: IdempotentRequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.TenantSummary>>(
        'POST',
        '/v1/admin/tenants',
        { body, ...options },
      ),

    /** List members for a tenant — `GET /v1/admin/tenants/:tenantId/members`. */
    listTenantMembers: (
      tenantId: string,
      params?: Omit<admin.ListTenantMembersParams, 'tenantId'>,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.TenantMemberSummary>>(
        'GET',
        `/v1/admin/tenants/${tenantId}/members`,
        { params: toSearchParams(params), ...options },
      ),

    /** List projects for a tenant — `GET /v1/admin/tenants/:tenantId/projects`. */
    listProjects: (
      tenantId: string,
      params?: Omit<admin.ListProjectsParams, 'tenantId'>,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.ProjectSummary>>(
        'GET',
        `/v1/admin/tenants/${tenantId}/projects`,
        { params: toSearchParams(params), ...options },
      ),

    /** Create a project — `POST /v1/admin/tenants/:tenantId/projects`. */
    createProject: (
      tenantId: string,
      body: admin.CreateProjectRequest,
      options: IdempotentRequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.ProjectSummary>>(
        'POST',
        `/v1/admin/tenants/${tenantId}/projects`,
        { body, ...options },
      ),

    /** List cases for a project — `GET /v1/admin/projects/:projectId/cases`. */
    listCases: (
      projectId: string,
      params?: Omit<admin.ListCasesParams, 'projectId'>,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.CaseSummary>>(
        'GET',
        `/v1/admin/projects/${projectId}/cases`,
        { params: toSearchParams(params), ...options },
      ),

    /** Get case detail — `GET /v1/admin/cases/:caseId`. */
    getCase: (caseId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.CaseDetail>>(
        'GET',
        `/v1/admin/cases/${caseId}`,
        options,
      ),

    /** List evidence for a case — `GET /v1/admin/cases/:caseId/evidence`. */
    listCaseEvidence: (
      caseId: string,
      params?: Omit<admin.ListEvidenceParams, 'caseId'>,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.EvidenceSummary>>(
        'GET',
        `/v1/admin/cases/${caseId}/evidence`,
        { params: toSearchParams(params), ...options },
      ),

    /** Create a decision on a case — `POST /v1/admin/cases/:caseId/decisions`. */
    createDecision: (
      caseId: string,
      body: admin.CreateDecisionRequest,
      options: IdempotentRequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.DecisionSummary>>(
        'POST',
        `/v1/admin/cases/${caseId}/decisions`,
        { body, ...options },
      ),

    /** List timeline events for a case — `GET /v1/admin/cases/:caseId/timeline`. */
    getCaseTimeline: (caseId: string, options?: RequestOptions) =>
      this.request<ApiListResponse<Record<string, unknown>>>(
        'GET',
        `/v1/admin/cases/${caseId}/timeline`,
        options,
      ),

    /** List screening requests for a case — `GET /v1/admin/cases/:caseId/screening`. */
    getCaseScreening: (
      caseId: string,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.ScreeningRequestSummary>>(
        'GET',
        `/v1/admin/cases/${caseId}/screening`,
        options,
      ),

    /** List decisions for a case — `GET /v1/admin/cases/:caseId/decisions`. */
    getCaseDecisions: (caseId: string, options?: RequestOptions) =>
      this.request<ApiListResponse<admin.DecisionSummary>>(
        'GET',
        `/v1/admin/cases/${caseId}/decisions`,
        options,
      ),

    /** List review notes for a case — `GET /v1/admin/cases/:caseId/notes`. */
    getCaseNotes: (caseId: string, options?: RequestOptions) =>
      this.request<ApiListResponse<admin.ReviewNoteSummary>>(
        'GET',
        `/v1/admin/cases/${caseId}/notes`,
        options,
      ),

    /** Create a review note on a case — `POST /v1/admin/cases/:caseId/notes`. */
    createNote: (
      caseId: string,
      body: admin.CreateReviewNoteRequest,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.ReviewNoteSummary>>(
        'POST',
        `/v1/admin/cases/${caseId}/notes`,
        { body, ...options },
      ),

    /** List review tasks for a project — `GET /v1/admin/projects/:projectId/review-tasks`. */
    listReviewTasks: (
      projectId: string,
      params?: Omit<admin.ListReviewTasksParams, 'projectId'>,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.ReviewTaskSummary>>(
        'GET',
        `/v1/admin/projects/${projectId}/review-tasks`,
        { params: toSearchParams(params), ...options },
      ),

    /** Assign a review task — `POST /v1/admin/review-tasks/:taskId/assign`. */
    assignReviewTask: (
      taskId: string,
      body: admin.AssignReviewTaskRequest,
      options: IdempotentRequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.AssignReviewTaskResponse>>(
        'POST',
        `/v1/admin/review-tasks/${taskId}/assign`,
        { body, ...options },
      ),

    /** List audit events — `GET /v1/admin/audit-events`. */
    listAuditEvents: (
      params?: admin.ListAuditEventsParams,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.AuditEventSummary>>(
        'GET',
        '/v1/admin/audit-events',
        { params: toSearchParams(params), ...options },
      ),

    /** List platform members — `GET /v1/admin/platform-members`. */
    listPlatformMembers: (
      params?: admin.ListPlatformMembersParams,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.PlatformMemberSummary>>(
        'GET',
        '/v1/admin/platform-members',
        { params: toSearchParams(params), ...options },
      ),

    /** Invite a platform member — `POST /v1/admin/platform-members/invite`. */
    invitePlatformMember: (
      body: admin.InvitePlatformMemberRequest,
      options: IdempotentRequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.PlatformMemberSummary>>(
        'POST',
        '/v1/admin/platform-members/invite',
        { body, ...options },
      ),

    /** List support sessions — `GET /v1/admin/support-sessions`. */
    listSupportSessions: (
      params?: admin.ListSupportSessionsParams,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.SupportSessionSummary>>(
        'GET',
        '/v1/admin/support-sessions',
        { params: toSearchParams(params), ...options },
      ),

    /** List domains for a project — `GET /v1/admin/projects/:projectId/domains`. */
    listDomains: (
      projectId: string,
      params?: Omit<admin.ListDomainsParams, 'projectId'>,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.DomainSummary>>(
        'GET',
        `/v1/admin/projects/${projectId}/domains`,
        { params: toSearchParams(params), ...options },
      ),

    /** Create a domain for a project — `POST /v1/admin/projects/:projectId/domains`. */
    createDomain: (
      projectId: string,
      body: admin.CreateDomainRequest,
      options: IdempotentRequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.DomainSummary>>(
        'POST',
        `/v1/admin/projects/${projectId}/domains`,
        { body, ...options },
      ),

    /** Verify a project domain — `POST /v1/admin/project-domains/:domainId/verify`. */
    verifyDomain: (
      domainId: string,
      body: admin.VerifyDomainRequest,
      options: IdempotentRequestOptions,
    ) =>
      this.request<ApiSuccessResponse<Record<string, unknown>>>(
        'POST',
        `/v1/admin/project-domains/${domainId}/verify`,
        { body, ...options },
      ),

    /** List SSO connections for a tenant — `GET /v1/admin/tenants/:tenantId/sso-connections`. */
    listSsoConnections: (
      tenantId: string,
      params?: Omit<admin.ListSsoConnectionsParams, 'tenantId'>,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.SsoConnectionSummary>>(
        'GET',
        `/v1/admin/tenants/${tenantId}/sso-connections`,
        { params: toSearchParams(params), ...options },
      ),

    /** Create an SSO connection — `POST /v1/admin/tenants/:tenantId/sso-connections`. */
    createSsoConnection: (
      tenantId: string,
      body: admin.CreateSsoConnectionRequest,
      options: IdempotentRequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.SsoConnectionSummary>>(
        'POST',
        `/v1/admin/tenants/${tenantId}/sso-connections`,
        { body, ...options },
      ),

    /** Create a provisioning connection — `POST /v1/admin/tenants/:tenantId/provisioning-connections`. */
    createProvisioningConnection: (
      tenantId: string,
      body: admin.CreateProvisioningConnectionRequest,
      options: IdempotentRequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.ProvisioningConnectionSummary>>(
        'POST',
        `/v1/admin/tenants/${tenantId}/provisioning-connections`,
        { body, ...options },
      ),

    /** List config versions — `GET /v1/admin/config-versions`. */
    listConfigVersions: (
      params?: admin.ListConfigVersionsParams,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.ConfigVersionSummary>>(
        'GET',
        '/v1/admin/config-versions',
        { params: toSearchParams(params), ...options },
      ),

    /** Promote a config version — `POST /v1/admin/config-versions/:configVersionId/promote`. */
    promoteConfigVersion: (
      configVersionId: string,
      body: admin.PromoteConfigVersionRequest,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.ConfigVersionSummary>>(
        'POST',
        `/v1/admin/config-versions/${configVersionId}/promote`,
        { body, ...options },
      ),

    /** Grant a platform role binding — `POST /v1/admin/platform-members/:platformMemberId/role-bindings`. */
    grantPlatformRoleBinding: (
      platformMemberId: string,
      body: admin.GrantPlatformRoleBindingRequest,
      options: IdempotentRequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.RoleBindingSummary>>(
        'POST',
        `/v1/admin/platform-members/${platformMemberId}/role-bindings`,
        { body, ...options },
      ),

    /** Grant a project role binding — `POST /v1/admin/projects/:projectId/role-bindings`. */
    grantProjectRoleBinding: (
      projectId: string,
      body: admin.GrantProjectRoleBindingRequest,
      options: IdempotentRequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.RoleBindingSummary>>(
        'POST',
        `/v1/admin/projects/${projectId}/role-bindings`,
        { body, ...options },
      ),

    /** Request support access — `POST /v1/admin/support-access-grants`. */
    createSupportAccessGrant: (
      body: admin.RequestSupportAccessRequest,
      options: IdempotentRequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.SupportAccessGrantSummary>>(
        'POST',
        '/v1/admin/support-access-grants',
        { body, ...options },
      ),

    /** Start support session — `POST /v1/admin/support-access-grants/:grantId/sessions`. */
    createSupportSession: (
      grantId: string,
      body: admin.StartSupportSessionRequest,
      options: IdempotentRequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.SupportSessionSummary>>(
        'POST',
        `/v1/admin/support-access-grants/${grantId}/sessions`,
        { body, ...options },
      ),

    /** Resolve effective configs — `GET /v1/admin/effective-configs`. */
    getEffectiveConfigs: (
      params: admin.GetEffectiveConfigParams,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.EffectiveConfigResponse>>(
        'GET',
        '/v1/admin/effective-configs',
        { params: toSearchParams(params), ...options },
      ),

    /** List webhook endpoints for a tenant — `GET /v1/admin/tenants/:tenantId/webhooks`. */
    listWebhooks: (
      tenantId: string,
      params?: admin.ListWebhookEndpointsParams,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.WebhookEndpointSummary>>(
        'GET',
        `/v1/admin/tenants/${tenantId}/webhooks`,
        { params: toSearchParams(params), ...options },
      ),

    /** Create a webhook endpoint — `POST /v1/admin/tenants/:tenantId/webhooks`. */
    createWebhook: (
      tenantId: string,
      body: admin.CreateWebhookEndpointRequest,
      options: IdempotentRequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.CreateWebhookEndpointResponse>>(
        'POST',
        `/v1/admin/tenants/${tenantId}/webhooks`,
        { body, ...options },
      ),

    /** Update a webhook endpoint — `PUT /v1/admin/tenants/:tenantId/webhooks/:webhookId`. */
    updateWebhook: (
      tenantId: string,
      webhookId: string,
      body: admin.UpdateWebhookEndpointRequest,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.UpdateWebhookEndpointResponse>>(
        'PUT',
        `/v1/admin/tenants/${tenantId}/webhooks/${webhookId}`,
        { body, ...options },
      ),

    /** Delete/disable a webhook endpoint — `DELETE /v1/admin/tenants/:tenantId/webhooks/:webhookId`. */
    deleteWebhook: (
      tenantId: string,
      webhookId: string,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.DeleteWebhookEndpointResponse>>(
        'DELETE',
        `/v1/admin/tenants/${tenantId}/webhooks/${webhookId}`,
        options,
      ),

    /** List webhook deliveries — `GET /v1/admin/tenants/:tenantId/webhooks/:webhookId/deliveries`. */
    listWebhookDeliveries: (
      tenantId: string,
      webhookId: string,
      params?: admin.ListWebhookDeliveriesParams,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.WebhookDeliverySummary>>(
        'GET',
        `/v1/admin/tenants/${tenantId}/webhooks/${webhookId}/deliveries`,
        { params: toSearchParams(params), ...options },
      ),

    /** Replay a webhook delivery — `POST /v1/admin/tenants/:tenantId/webhooks/:webhookId/deliveries/:deliveryId/replay`. */
    replayWebhookDelivery: (
      tenantId: string,
      webhookId: string,
      deliveryId: string,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.ReplayWebhookDeliveryResponse>>(
        'POST',
        `/v1/admin/tenants/${tenantId}/webhooks/${webhookId}/deliveries/${deliveryId}/replay`,
        options,
      ),

    // ---- KYB: Business Entities ----

    /** List business entities — `GET /v1/admin/business-entities`. */
    listBusinessEntities: (
      params?: admin.ListBusinessEntitiesParams,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.BusinessEntitySummary>>(
        'GET',
        '/v1/admin/business-entities',
        { params: toSearchParams(params), ...options },
      ),

    /** Create a business entity — `POST /v1/admin/business-entities`. */
    createBusinessEntity: (
      body: admin.CreateBusinessEntityRequest,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.BusinessEntitySummary>>(
        'POST',
        '/v1/admin/business-entities',
        { body, ...options },
      ),

    /** Get business entity detail — `GET /v1/admin/business-entities/:entityId`. */
    getBusinessEntity: (entityId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.BusinessEntitySummary>>(
        'GET',
        `/v1/admin/business-entities/${entityId}`,
        options,
      ),

    /** Get ownership graph — `GET /v1/admin/business-entities/:entityId/ownership-graph`. */
    getOwnershipGraph: (entityId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.OwnershipGraphResponse>>(
        'GET',
        `/v1/admin/business-entities/${entityId}/ownership-graph`,
        options,
      ),

    /** List fraud signals — `GET /v1/admin/cases/:caseId/fraud-signals`. */
    listFraudSignals: (caseId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.ListFraudSignalsResponse>>(
        'GET',
        `/v1/admin/cases/${caseId}/fraud-signals`,
        options,
      ),

    /** Trigger step-up — `POST /v1/admin/cases/:caseId/step-up`. */
    triggerStepUp: (
      caseId: string,
      body: admin.TriggerStepUpRequest,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.TriggerStepUpResponse>>(
        'POST',
        `/v1/admin/cases/${caseId}/step-up`,
        { body, ...options },
      ),

    /** List supplemental docs — `GET /v1/admin/cases/:caseId/supplemental-docs`. */
    listSupplementalDocs: (
      caseId: string,
      params?: Omit<admin.ListSupplementalDocsParams, 'caseId'>,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.SupplementalDocRequest>>(
        'GET',
        `/v1/admin/cases/${caseId}/supplemental-docs`,
        { params: toSearchParams(params), ...options },
      ),

    /** Create supplemental doc request — `POST /v1/admin/cases/:caseId/supplemental-docs`. */
    createSupplementalDocRequest: (
      caseId: string,
      body: admin.CreateSupplementalDocRequestInput,
      options: IdempotentRequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.SupplementalDocResponse>>(
        'POST',
        `/v1/admin/cases/${caseId}/supplemental-docs`,
        { body, ...options },
      ),

    /** Upload supplemental doc metadata — `POST /v1/admin/supplemental-docs/:docId/upload`. */
    uploadSupplementalDoc: (
      docId: string,
      body: Record<string, unknown>,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.SupplementalDocResponse>>(
        'POST',
        `/v1/admin/supplemental-docs/${docId}/upload`,
        { body, ...options },
      ),

    // ---- KYM: Merchant Profiles ----

    /** List merchant profiles — `GET /v1/admin/merchant-profiles`. */
    listMerchantProfiles: (
      params?: admin.ListMerchantProfilesParams,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.MerchantProfileSummary>>(
        'GET',
        '/v1/admin/merchant-profiles',
        { params: toSearchParams(params), ...options },
      ),

    /** Create a merchant profile — `POST /v1/admin/merchant-profiles`. */
    createMerchantProfile: (
      body: admin.CreateMerchantProfileRequest,
      options: IdempotentRequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.MerchantProfileSummary>>(
        'POST',
        '/v1/admin/merchant-profiles',
        { body, ...options },
      ),

    /** Get merchant profile detail — `GET /v1/admin/merchant-profiles/:profileId`. */
    getMerchantProfile: (profileId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.MerchantProfileSummary>>(
        'GET',
        `/v1/admin/merchant-profiles/${profileId}`,
        options,
      ),

    /** List merchant risk signals — `GET /v1/admin/merchant-profiles/:profileId/risk-signals`. */
    listMerchantRiskSignals: (
      profileId: string,
      params?: PaginationParams,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.MerchantRiskSignalSummary>>(
        'GET',
        `/v1/admin/merchant-profiles/${profileId}/risk-signals`,
        { params: toSearchParams(params), ...options },
      ),

    /** List merchant capabilities — `GET /v1/admin/merchant-profiles/:profileId/capabilities`. */
    listMerchantCapabilities: (
      profileId: string,
      params?: PaginationParams,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.MerchantCapabilitySummary>>(
        'GET',
        `/v1/admin/merchant-profiles/${profileId}/capabilities`,
        { params: toSearchParams(params), ...options },
      ),

    // ---- KYM: Merchant Review Views ----

    /** List merchants — `GET /v1/admin/merchants`. */
    listMerchants: (
      params?: admin.ListMerchantProfilesParams,
      options?: RequestOptions,
    ) =>
      this.request<ApiListResponse<admin.MerchantProfileSummary>>(
        'GET',
        '/v1/admin/merchants',
        { params: toSearchParams(params), ...options },
      ),

    /** Get merchant — `GET /v1/admin/merchants/:merchantId`. */
    getMerchant: (merchantId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.MerchantProfileSummary>>(
        'GET',
        `/v1/admin/merchants/${merchantId}`,
        options,
      ),

    /** Get merchant risk — `GET /v1/admin/merchants/:merchantId/risk`. */
    getMerchantRisk: (merchantId: string, params?: PaginationParams, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.MerchantRiskResponse>>(
        'GET',
        `/v1/admin/merchants/${merchantId}/risk`,
        { params: toSearchParams(params), ...options },
      ),

    /** List merchant events — `GET /v1/admin/merchants/:merchantId/events`. */
    listMerchantEvents: (merchantId: string, params?: PaginationParams, options?: RequestOptions) =>
      this.request<ApiListResponse<admin.MerchantRiskSignalSummary>>(
        'GET',
        `/v1/admin/merchants/${merchantId}/events`,
        { params: toSearchParams(params), ...options },
      ),

    /** Create merchant review — `POST /v1/admin/merchants/:merchantId/review`. */
    createMerchantReview: (
      merchantId: string,
      body: admin.CreateMerchantReviewRequest,
      options: IdempotentRequestOptions,
    ) =>
      this.request<ApiSuccessResponse<admin.MerchantReviewResponse>>(
        'POST',
        `/v1/admin/merchants/${merchantId}/review`,
        { body, ...options },
      ),

    // ---- Monitoring ----

    /** List monitoring schedules — `GET /v1/admin/monitoring/schedules`. */
    listMonitoringSchedules: (params?: admin.ListMonitoringSchedulesParams & { tenantId?: string }, options?: RequestOptions) =>
      this.request<ApiListResponse<admin.MonitoringScheduleResponse>>(
        'GET',
        '/v1/admin/monitoring/schedules',
        { params: toSearchParams(params), ...options },
      ),

    /** Create monitoring schedule — `POST /v1/admin/monitoring/schedules`. */
    createMonitoringSchedule: (body: admin.CreateMonitoringScheduleInput, options: IdempotentRequestOptions) =>
      this.request<ApiSuccessResponse<admin.MonitoringScheduleResponse>>(
        'POST',
        '/v1/admin/monitoring/schedules',
        { body, ...options },
      ),

    /** Update monitoring schedule — `PUT /v1/admin/monitoring/schedules/:id`. */
    updateMonitoringSchedule: (id: string, body: admin.UpdateMonitoringScheduleInput, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<{ id: string; updatedAt: string }>>(
        'PUT',
        `/v1/admin/monitoring/schedules/${id}`,
        { body, ...options },
      ),

    /** List monitoring alerts — `GET /v1/admin/monitoring/alerts`. */
    listMonitoringAlerts: (params?: admin.ListMonitoringAlertsParams & { tenantId?: string }, options?: RequestOptions) =>
      this.request<ApiListResponse<admin.MonitoringAlertResponse>>(
        'GET',
        '/v1/admin/monitoring/alerts',
        { params: toSearchParams(params), ...options },
      ),

    /** Resolve monitoring alert — `POST /v1/admin/monitoring/alerts/:alertId/resolve`. */
    resolveMonitoringAlert: (alertId: string, body: admin.AlertResolutionInput, options: IdempotentRequestOptions) =>
      this.request<ApiSuccessResponse<Record<string, unknown>>>(
        'POST',
        `/v1/admin/monitoring/alerts/${alertId}/resolve`,
        { body, ...options },
      ),

    /** Get rescreening history — `GET /v1/admin/monitoring/entities/:entityId/rescreening`. */
    getRescreeningHistory: (entityId: string, params?: PaginationParams & { tenantId?: string }, options?: RequestOptions) =>
      this.request<ApiListResponse<admin.RescreeningResultResponse>>(
        'GET',
        `/v1/admin/monitoring/entities/${entityId}/rescreening`,
        { params: toSearchParams(params), ...options },
      ),

    /** Get alert stats — `GET /v1/admin/monitoring/alerts/stats`. */
    getMonitoringAlertStats: (params?: { entityId?: string; tenantId?: string }, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.AlertStatsResponse>>(
        'GET',
        '/v1/admin/monitoring/alerts/stats',
        { params: toSearchParams(params), ...options },
      ),

    // ---- AML ----

    /** Screen subject — `POST /v1/admin/aml/screen`. */
    amlScreen: (body: admin.AmlScreenRequest, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.AmlScreenResponse>>('POST', '/v1/admin/aml/screen', { body, ...options }),

    /** Run AML fraud check — `POST /v1/admin/aml/fraud-check`. */
    amlFraudCheck: (body: admin.AmlFraudCheckRequest, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.AmlFraudCheckResponse>>('POST', '/v1/admin/aml/fraud-check', { body, ...options }),

    /** Generate STR report — `POST /v1/admin/aml/str-report`. */
    amlStrReport: (body: admin.AmlStrReportRequest, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.AmlStrReportResponse>>('POST', '/v1/admin/aml/str-report', { body, ...options }),

    /** Mark AML hit false-positive — `POST /v1/admin/aml/hit/:hitId/false-positive`. */
    amlFalsePositive: (hitId: string, body: admin.AmlFalsePositiveRequest, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.AmlFalsePositiveResponse>>('POST', `/v1/admin/aml/hit/${hitId}/false-positive`, { body, ...options }),

    /** List AML exceptions — `GET /v1/admin/aml/exceptions`. */
    listAmlExceptions: (params?: admin.ListAmlExceptionsParams, options?: RequestOptions) =>
      this.request<ApiListResponse<admin.AmlExceptionRecord>>('GET', '/v1/admin/aml/exceptions', { params: toSearchParams(params), ...options }),

    // ---- Analytics & System ----

    /** Get dashboard metrics — `GET /v1/admin/analytics/metrics`. */
    getDashboardMetrics: (params: admin.AnalyticsRangeParams, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.DashboardMetricsResponse>>(
        'GET',
        '/v1/admin/analytics/metrics',
        { params: toSearchParams(params), ...options },
      ),

    /** Get conversion funnel — `GET /v1/admin/analytics/funnel`. */
    getConversionFunnel: (params: admin.AnalyticsRangeParams, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.FunnelStageResponse[]>>(
        'GET',
        '/v1/admin/analytics/funnel',
        { params: toSearchParams(params), ...options },
      ),

    /** Get provider performance — `GET /v1/admin/analytics/providers`. */
    getProviderPerformance: (params?: Partial<admin.AnalyticsRangeParams>, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.ProviderPerformanceResponse[]>>(
        'GET',
        '/v1/admin/analytics/providers',
        { params: toSearchParams(params), ...options },
      ),

    /** Get analytics time series — `GET /v1/admin/analytics/timeseries`. */
    getTimeSeries: (params: admin.TimeSeriesParams, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.TimeSeriesPointResponse[]>>(
        'GET',
        '/v1/admin/analytics/timeseries',
        { params: toSearchParams(params), ...options },
      ),

    /** Get system health — `GET /v1/admin/system/health`. */
    getSystemHealth: (options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.SystemHealthResponse>>(
        'GET',
        '/v1/admin/system/health',
        options,
      ),

    /** List system alerts — `GET /v1/admin/system/alerts`. */
    listSystemAlerts: (options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.SystemAlertResponse[]>>(
        'GET',
        '/v1/admin/system/alerts',
        options,
      ),

    /** Acknowledge system alert — `POST /v1/admin/system/alerts/:alertId/acknowledge`. */
    acknowledgeSystemAlert: (alertId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.AcknowledgeSystemAlertResponse>>(
        'POST',
        `/v1/admin/system/alerts/${alertId}/acknowledge`,
        options,
      ),

    // ---- AI Assist ----

    /** Generate AI case summary — `POST /v1/admin/cases/:caseId/ai/summary`. */
    generateAiCaseSummary: (caseId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.AiCaseSummaryResponse>>(
        'POST',
        `/v1/admin/cases/${caseId}/ai/summary`,
        options,
      ),

    /** Triage watchlist hit — `POST /v1/admin/screening/:hitId/ai/triage`. */
    triageWatchlistHit: (hitId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.AiWatchlistTriageResponse>>(
        'POST',
        `/v1/admin/screening/${hitId}/ai/triage`,
        options,
      ),

    /** Analyze evidence — `POST /v1/admin/evidence/:evidenceId/ai/analyze`. */
    analyzeEvidence: (evidenceId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.AiEvidenceAnalysisResponse>>(
        'POST',
        `/v1/admin/evidence/${evidenceId}/ai/analyze`,
        options,
      ),

    /** List AI audit log — `GET /v1/admin/ai/audit-log`. */
    listAiAuditLog: (params?: admin.AiAuditLogParams, options?: RequestOptions) =>
      this.request<ApiListResponse<admin.AiAuditLogRecord>>(
        'GET',
        '/v1/admin/ai/audit-log',
        { params: toSearchParams(params), ...options },
      ),

    /** List AI feature flags — `GET /v1/admin/ai/features`. */
    listAiFeatureFlags: (options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.AiFeatureFlag[]>>(
        'GET',
        '/v1/admin/ai/features',
        options,
      ),

    /** Update AI feature flag — `PUT /v1/admin/ai/features/:feature`. */
    updateAiFeatureFlag: (feature: string, body: admin.UpdateAiFeatureFlagRequest, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.AiFeatureFlag>>(
        'PUT',
        `/v1/admin/ai/features/${feature}`,
        { body, ...options },
      ),

    // ---- Enterprise ----

    /** List runbooks — `GET /v1/admin/enterprise/runbooks`. */
    listRunbooks: (options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.RunbookResponse[]>>(
        'GET',
        '/v1/admin/enterprise/runbooks',
        options,
      ),

    /** Start runbook — `POST /v1/admin/enterprise/runbooks/:runbookId/start`. */
    startRunbook: (runbookId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.RunbookExecutionResponse>>(
        'POST',
        `/v1/admin/enterprise/runbooks/${runbookId}/start`,
        options,
      ),

    /** List legal holds — `GET /v1/admin/enterprise/legal-holds`. */
    listLegalHolds: (params?: admin.LegalHoldParams, options?: RequestOptions) =>
      this.request<ApiListResponse<admin.LegalHoldResponse>>(
        'GET',
        '/v1/admin/enterprise/legal-holds',
        { params: toSearchParams(params), ...options },
      ),

    /** Create legal hold — `POST /v1/admin/enterprise/legal-holds`. */
    createLegalHold: (body: admin.CreateLegalHoldRequest, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.LegalHoldResponse>>(
        'POST',
        '/v1/admin/enterprise/legal-holds',
        { body, ...options },
      ),

    /** Release legal hold — `POST /v1/admin/enterprise/legal-holds/:holdId/release`. */
    releaseLegalHold: (holdId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.ReleaseLegalHoldResponse>>(
        'POST',
        `/v1/admin/enterprise/legal-holds/${holdId}/release`,
        options,
      ),

    /** Create data export — `POST /v1/admin/enterprise/data-exports`. */
    createDataExport: (body: admin.CreateDataExportRequest, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.DataExportResponse>>(
        'POST',
        '/v1/admin/enterprise/data-exports',
        { body, ...options },
      ),

    /** Get data export — `GET /v1/admin/enterprise/data-exports/:exportId`. */
    getDataExport: (exportId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.DataExportResponse>>(
        'GET',
        `/v1/admin/enterprise/data-exports/${exportId}`,
        options,
      ),

    /** List canary releases — `GET /v1/admin/enterprise/releases`. */
    listCanaryReleases: (params?: admin.CanaryReleaseParams, options?: RequestOptions) =>
      this.request<ApiListResponse<admin.CanaryReleaseResponse>>(
        'GET',
        '/v1/admin/enterprise/releases',
        { params: toSearchParams(params), ...options },
      ),

    /** Promote canary release — `POST /v1/admin/enterprise/releases/:releaseId/promote`. */
    promoteCanary: (releaseId: string, body: admin.PromoteCanaryRequest, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.PromoteCanaryResponse>>(
        'POST',
        `/v1/admin/enterprise/releases/${releaseId}/promote`,
        { body, ...options },
      ),

    /** Roll back canary release — `POST /v1/admin/enterprise/releases/:releaseId/rollback`. */
    rollbackCanary: (releaseId: string, body: admin.RollbackCanaryRequest, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<admin.RollbackCanaryResponse>>(
        'POST',
        `/v1/admin/enterprise/releases/${releaseId}/rollback`,
        { body, ...options },
      ),
  };

  // ---- Public API (no auth required) ----

  public = {
    /** Submit a demo request / lead — `POST /v1/public/leads`. */
    submitLead: (
      body: {
        name: string;
        email: string;
        company?: string;
        message?: string;
      },
      options?: RequestOptions,
    ) =>
      this.request<
        ApiSuccessResponse<{ id: string; message: string }>
      >('POST', '/v1/public/leads', { body, ...options }),
  };

  // ---- Applicant API ----

  applicant = {
    /** Get project context — `GET /v1/applicant/context`. */
    getContext: (options?: RequestOptions) =>
      this.request<ApiSuccessResponse<applicant.ApplicantContext>>(
        'GET',
        '/v1/applicant/context',
        options,
      ),

    /** Create a case — `POST /v1/applicant/cases`. */
    createCase: (
      body: applicant.CreateCaseRequest,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<applicant.CaseSummary>>(
        'POST',
        '/v1/applicant/cases',
        { body, ...options },
      ),

    /** Get case detail — `GET /v1/applicant/cases/:caseId`. */
    getCase: (caseId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<applicant.CaseDetail>>(
        'GET',
        `/v1/applicant/cases/${caseId}`,
        options,
      ),

    /** List submission sets for a case — `GET /v1/applicant/cases/:caseId/submission-sets`. */
    listSubmissionSets: (caseId: string, options?: RequestOptions) =>
      this.request<ApiListResponse<applicant.SubmissionSetSummary>>(
        'GET',
        `/v1/applicant/cases/${caseId}/submission-sets`,
        options,
      ),

    /** Create a submission set — `POST /v1/applicant/cases/:caseId/submission-sets`. */
    createSubmissionSet: (caseId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<applicant.SubmissionSetSummary>>(
        'POST',
        `/v1/applicant/cases/${caseId}/submission-sets`,
        options,
      ),

    /** Request an upload URL — `POST /v1/applicant/submission-sets/:submissionSetId/uploads`. */
    requestUploadUrl: (
      submissionSetId: string,
      body: Omit<applicant.RequestUploadUrlRequest, 'submissionSetId'>,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<applicant.UploadUrlResponse>>(
        'POST',
        `/v1/applicant/submission-sets/${submissionSetId}/uploads`,
        { body, ...options },
      ),

    /** Confirm an upload is complete — `POST /v1/applicant/uploads/:uploadId/complete`. */
    confirmUpload: (
      uploadId: string,
      body?: applicant.ConfirmUploadRequest,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<applicant.ConfirmUploadResponse>>(
        'POST',
        `/v1/applicant/uploads/${uploadId}/complete`,
        { body, ...options },
      ),

    /** Start a biometric check — `POST /v1/applicant/biometrics/selfie-check`. */
    startBiometricCheck: (
      body: applicant.StartBiometricCheckRequest,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<applicant.BiometricCheckSummary>>(
        'POST',
        '/v1/applicant/biometrics/selfie-check',
        { body, ...options },
      ),

    /** Request a liveness challenge — `POST /v1/applicant/biometrics/liveness-challenge`. */
    requestLivenessChallenge: (
      caseId: string,
      options?: RequestOptions,
    ) =>
      this.request<applicant.LivenessChallengeResponse>(
        'POST',
        '/v1/applicant/biometrics/liveness-challenge',
        { body: { caseId }, ...options },
      ),

    /** Submit a liveness challenge — `POST /v1/applicant/biometrics/liveness-challenge/submit`. */
    submitLivenessChallenge: (
      body: applicant.SubmitLivenessChallengeRequest,
      options?: RequestOptions,
    ) =>
      this.request<applicant.StartBiometricCheckResponse>(
        'POST',
        '/v1/applicant/biometrics/liveness-challenge/submit',
        { body, ...options },
      ),

    /** Get personal details — `GET /v1/applicant/cases/:caseId/personal-details`. */
    getPersonalDetails: (caseId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<applicant.PersonalDetailsData>>(
        'GET',
        `/v1/applicant/cases/${caseId}/personal-details`,
        options,
      ),

    /** Save personal details — `POST /v1/applicant/cases/:caseId/personal-details`. */
    savePersonalDetails: (
      caseId: string,
      body: applicant.PersonalDetailsInput,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<applicant.PersonalDetailsData>>(
        'POST',
        `/v1/applicant/cases/${caseId}/personal-details`,
        { body, ...options },
      ),

    /** Get KYB application — `GET /v1/applicant/cases/:caseId/kyb-application`. */
    getKybApplication: (caseId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<applicant.KybApplicationData>>(
        'GET',
        `/v1/applicant/cases/${caseId}/kyb-application`,
        options,
      ),

    /** Save KYB application — `POST /v1/applicant/cases/:caseId/kyb-application`. */
    saveKybApplication: (
      caseId: string,
      body: applicant.KybApplicationInput,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<applicant.KybApplicationData>>(
        'POST',
        `/v1/applicant/cases/${caseId}/kyb-application`,
        { body, ...options },
      ),

    /** Get KYM application — `GET /v1/applicant/cases/:caseId/kym-application`. */
    getKymApplication: (caseId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<applicant.KymApplicationData>>(
        'GET',
        `/v1/applicant/cases/${caseId}/kym-application`,
        options,
      ),

    /** Save KYM application — `POST /v1/applicant/cases/:caseId/kym-application`. */
    saveKymApplication: (
      caseId: string,
      body: applicant.KymApplicationInput,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<applicant.KymApplicationData>>(
        'POST',
        `/v1/applicant/cases/${caseId}/kym-application`,
        { body, ...options },
      ),

    /** Get document requirements — `GET /v1/applicant/cases/:caseId/document-requirements`. */
    getDocumentRequirements: (caseId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<applicant.GetDocumentRequirementsResponse>>(
        'GET',
        `/v1/applicant/cases/${caseId}/document-requirements`,
        options,
      ),

    /** Request data erasure (KVKK/GDPR) — `DELETE /v1/applicant/cases/:caseId/personal-data`. */
    requestDataErasure: (caseId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<applicant.DataErasureResponse>>(
        'DELETE',
        `/v1/applicant/cases/${caseId}/personal-data`,
        options,
      ),

    /** Record consent — `POST /v1/applicant/cases/:caseId/consent`. */
    recordConsent: (
      caseId: string,
      body: applicant.RecordConsentRequest,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<applicant.ConsentRecord>>(
        'POST',
        `/v1/applicant/cases/${caseId}/consent`,
        { body, ...options },
      ),

    /** Withdraw consent — `POST /v1/applicant/cases/:caseId/consent/withdraw`. */
    withdrawConsent: (
      caseId: string,
      body: applicant.WithdrawConsentRequest,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<applicant.ConsentRecord>>(
        'POST',
        `/v1/applicant/cases/${caseId}/consent/withdraw`,
        { body, ...options },
      ),

    /** List consents for a case — `GET /v1/applicant/cases/:caseId/consents`. */
    listConsents: (caseId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<applicant.ConsentRecord[]>>(
        'GET',
        `/v1/applicant/cases/${caseId}/consents`,
        options,
      ),

    /** Submit a Data Subject Request — `POST /v1/applicant/cases/:caseId/dsr`. */
    submitDsr: (
      caseId: string,
      body: applicant.SubmitDsrRequest,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<applicant.DsrRecord>>(
        'POST',
        `/v1/applicant/cases/${caseId}/dsr`,
        { body, ...options },
      ),

    /** List DSRs for a case — `GET /v1/applicant/cases/:caseId/dsr`. */
    listDsr: (caseId: string, options?: RequestOptions) =>
      this.request<ApiSuccessResponse<applicant.DsrRecord[]>>(
        'GET',
        `/v1/applicant/cases/${caseId}/dsr`,
        options,
      ),

    /** Get a specific DSR — `GET /v1/applicant/cases/:caseId/dsr/:dsrId`. */
    getDsr: (
      caseId: string,
      dsrId: string,
      options?: RequestOptions,
    ) =>
      this.request<ApiSuccessResponse<applicant.DsrRecord>>(
        'GET',
        `/v1/applicant/cases/${caseId}/dsr/${dsrId}`,
        options,
      ),
  };
}

/** Create a client instance with the given configuration. */
export function createNoxVerifyClient(
  config: ApiClientConfig,
): NoxVerifyClient {
  return new NoxVerifyClient(config);
}
